import React, { useEffect } from 'react'
import { Box, Text, Link } from 'pcln-design-system'
import pclnExperimentation from '@pcln/experimentation'
import queryString from 'query-string'
import useSeti from '@/hooks/useSeti'
import { trackPackageSearchLinkClick, trackPackageSearchLinkView } from '../ga4'
import type { MakeWatchObject, PackagesFormStateType } from '../types'
import { isHotelPlusCar } from './TripTypes'

type AdvancedSearchLinkProps = Readonly<{
  pageName: 'homepage' | 'landing'
  watchState: MakeWatchObject<PackagesFormStateType>
}>

type FormatPackageSearchHref = Pick<
  MakeWatchObject<PackagesFormStateType>,
  | 'watchTripType'
  | 'watchStartLocation'
  | 'watchEndLocation'
  | 'watchStartDate'
  | 'watchEndDate'
  | 'watchTravelers'
  | 'watchHotelStartDate'
  | 'watchHotelEndDate'
  | 'watchPartialStay'
>

function formatPackageSearchHref(
  {
    watchTripType,
    watchStartLocation: startLocation,
    watchEndLocation: endLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay
  }: FormatPackageSearchHref,
  redirectSource: 'landing' | 'homepage'
) {
  const stringifiedQuery = queryString.stringify({
    'redirect-source': redirectSource,
    'flight-code': 'RT',
    'trip-type': watchTripType,
    'origin-item': startLocation?.itemName,
    'origin-id': startLocation?.id,
    'origin-type': startLocation?.type,
    'origin-city': startLocation?.cityName,
    'origin-state': startLocation?.provinceName,
    'origin-state-code': startLocation?.stateCode,
    'origin-country': startLocation?.country,
    'origin-country-code': startLocation?.countryCode,
    'origin-latitude': startLocation?.lat,
    'origin-longitude': startLocation?.lon,
    'destination-item': endLocation?.itemName,
    'destination-id': endLocation?.id,
    'destination-type': endLocation?.type,
    'destination-city': endLocation?.cityName,
    'destination-state': endLocation?.provinceName,
    'destination-state-code': endLocation?.stateCode,
    'destination-country': endLocation?.country,
    'destination-country-code': endLocation?.countryCode,
    'destination-latitude': endLocation?.lat,
    'destination-longitude': endLocation?.lon,
    start: watchStartDate,
    end: watchEndDate,
    rooms: watchTravelers?.rooms,
    adults: watchTravelers?.adults,
    children: watchTravelers?.children,
    ages: JSON.stringify(watchTravelers?.childrenAges),
    travelers: watchTravelers,
    ...(watchPartialStay
      ? {
          'hotel-start': watchHotelStartDate,
          'hotel-end': watchHotelEndDate
        }
      : {})
  })

  return `/shop/search/trips/?${stringifiedQuery}`
}

function AdvancedSearchLinkFlight({
  pageName,
  watchState
}: AdvancedSearchLinkProps) {
  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay
  } = watchState
  useEffect(() => {
    trackPackageSearchLinkView(pageName, watchTripType)
  }, [pageName, watchTripType])
  return (
    <Box>
      <Link
        href={formatPackageSearchHref(
          {
            watchTripType,
            watchStartLocation,
            watchEndLocation,
            watchStartDate,
            watchEndDate,
            watchTravelers,
            watchHotelStartDate,
            watchHotelEndDate,
            watchPartialStay
          },
          pageName
        )}
        target="_blank"
        onClick={() => trackPackageSearchLinkClick(pageName, watchTripType)}
      >
        <Text fontSize="14px" fontWeight="bold">
          Advanced Search (One-Way Flight, Multi-Hotel)
        </Text>
      </Link>
    </Box>
  )
}

function AdvancedSearchLinkHotelCar({
  pageName,
  watchState
}: AdvancedSearchLinkProps) {
  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay
  } = watchState
  const advancedSearchHotelCarLinkSeti =
    useSeti('PKG_HP_ADVANCED_SEARCH_HC', false) === 'VARIANT'
  useEffect(() => {
    pclnExperimentation.fireImpression('PKG_HP_ADVANCED_SEARCH_HC')
  }, [])
  useEffect(() => {
    if (advancedSearchHotelCarLinkSeti) {
      trackPackageSearchLinkView(pageName, watchTripType)
    }
  }, [pageName, watchTripType, advancedSearchHotelCarLinkSeti])
  return (
    advancedSearchHotelCarLinkSeti && (
      <Box>
        <Link
          href={formatPackageSearchHref(
            {
              watchTripType,
              watchStartLocation,
              watchEndLocation,
              watchStartDate,
              watchEndDate,
              watchTravelers,
              watchHotelStartDate,
              watchHotelEndDate,
              watchPartialStay
            },
            pageName
          )}
          target="_blank"
          onClick={() => trackPackageSearchLinkClick(pageName, watchTripType)}
        >
          <Text fontSize="14px" fontWeight="bold">
            Advanced Search (Multi-Hotel, Custom Car Dates)
          </Text>
        </Link>
      </Box>
    )
  )
}

export default function AdvancedSearchLink({
  pageName,
  watchState
}: AdvancedSearchLinkProps) {
  const { watchTripType } = watchState
  const showAdvancedSearchLinkHotelCar = isHotelPlusCar(watchTripType)
  return (
    <>
      {watchTripType.includes('FLY') && (
        <AdvancedSearchLinkFlight pageName={pageName} watchState={watchState} />
      )}
      {showAdvancedSearchLinkHotelCar && (
        <AdvancedSearchLinkHotelCar
          pageName={pageName}
          watchState={watchState}
        />
      )}
    </>
  )
}
